import dayjs from 'dayjs'
import localizedFormatPlugin from 'dayjs/plugin/localizedFormat'
import timezonePlugin from 'dayjs/plugin/timezone'
import utcPlugin from 'dayjs/plugin/utc'
import relativeTimePlugin from 'dayjs/plugin/relativeTime'
import localeDataPlugin from 'dayjs/plugin/localeData'

import { DayJSLocale } from './types'
import { LOCALE_BY_LANGUAGE } from './constants'

import { Language } from 'common/types'

import 'dayjs/locale/ar'
import 'dayjs/locale/en'
import 'dayjs/locale/es'
import 'dayjs/locale/fr'
import 'dayjs/locale/pt-br'

dayjs.extend(localizedFormatPlugin)
dayjs.extend(timezonePlugin)
dayjs.extend(utcPlugin)
dayjs.extend(relativeTimePlugin)
dayjs.extend(localeDataPlugin)

class DateHelper {
  static setLocale = (language: Language) => {
    const locale = LOCALE_BY_LANGUAGE[language] || DayJSLocale.EN

    dayjs.locale(locale)
  }

  static convertDateToLocalizedFormat = (date: dayjs.ConfigType, timezone: string): string => {
    try {
      return dayjs.utc(date).tz(timezone).format('MM/DD/YYYY LT')
    } catch (error) {
      console.error(error)

      return ''
    }
  }

  /**
   * Function that receives the datetime and timezone and
   * returns it in the format: "Thursday, August 29, 2019 3:21 PM"
   *
   * @param date - Date | string | number
   * @param timezone - string
   * @returns dateFormated - string
   */
  static convertISODateToLocaleFormatted = (date: dayjs.ConfigType, timezone: string): string => {
    try {
      return dayjs.utc(date).tz(timezone).format('LLLL')
    } catch (error) {
      console.error(error)

      return ''
    }
  }

  /**
   * Function that receives the datetime and timezone
   * or take the current date and time and timezone and
   * return in the format: "2014-06-01T12:00:00Z"
   *
   * @param date - Date | string | number
   * @param timezone - string
   * @returns dateFormated - string
   */
  static formatDateTimeToUtc = (
    date?: Date | string | number,
    timezone?: string
  ): string => {
    try {
      const dateTime = date ? date : new Date()
      const zone = timezone ? timezone : dayjs.tz.guess()

      return dayjs.tz(dateTime, zone).utc().format()
    } catch (error) {
      console.error(error)

      return ''
    }
  }

  /**
   * Function to return the current timezone
   *
   * @returns timezoneNames - any
   */
  static returnCurrentTimezone = () => {
    return dayjs.tz.guess()
  }

  static formatTimestampToTime = (timestamp: number): string => {
    const time = dayjs(timestamp).format('HH:mm')

    return time
  }

  static formatMilliseconds = (
    milliseconds: number,
    { hideSeconds = false } = {}
  ) => {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60) % 60
    const hours = Math.floor(seconds / 60 / 60)

    const padHours = DateHelper.padNumber(hours)
    const padMinutes = DateHelper.padNumber(minutes)
    const padSeconds = DateHelper.padNumber(seconds % 60)

    let formatted = `${padHours}:${padMinutes}`

    if (!hideSeconds) {
      formatted += `:${padSeconds}`
    }

    return formatted
  }

  static padNumber = (n: number): string => {
    return n.toString().padStart(2, '0')
  }

  static durationTime = (started: string, finished: string) => {
    const duration = DateHelper.formatToTimestamp(finished) - DateHelper.formatToTimestamp(started)
    const durationFormated = DateHelper.formatMilliseconds(duration)

    return durationFormated
  }

  static formatToTimestamp = (date: string) => {
    return new Date(date).getTime()
  }

  static getTimeFrom = (date: Date) => {
    const now = new Date()

    return now.getTime() - date.getTime()
  }

  static calculateTimeFields = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)

    return [hours, (minutes % 60), (seconds % 60)]
  }

  static format = (
    date: dayjs.ConfigType,
    desiredFormat?: string,
    { timezone }: { timezone?: string } = {}) => {
    try {
      let dateObj = dayjs(date)

      if (timezone) {
        dateObj = dateObj.tz(timezone)
      }

      return dateObj.format(desiredFormat)
    } catch (error) {
      console.error(error)

      return ''
    }
  }

  static toDate = (date: string, currentFormat: string) => {
    return dayjs(date, currentFormat).toDate()
  }

  static get locale() {
    return dayjs.locale()
  }

  static get weekdays() {
    return dayjs.weekdays()
  }

  static get weekdaysShort() {
    return dayjs.weekdaysShort()
  }

  static getRemainingTime(
    date: dayjs.ConfigType,
    unitOfTime: dayjs.OpUnitType = 'minute'
  ) {
    return dayjs(date).startOf(unitOfTime).fromNow()
  }

  static diff(start: Date, stop: Date = new Date()) {
    return stop.getTime() - start.getTime()
  }

  static padHourMinute = (hours: number, minutes: number) => {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }
}

export default DateHelper
